import { useMemo } from 'react';
import { Row, Typography } from 'antd';
import get from 'lodash/get';
import { parseProjectTitle } from 'utils/string.utils';
import StatusTag from 'components/StatusTag';
import useWebSocket from 'utils/useWebSocket';
import { useDispatch } from 'react-redux';
import { getProjectSelect } from '../../store/projectSelect-slice';

const { Text } = Typography;

const searchKeys = ['title', 'customer.business_name', 'customer.abbreviation', 'subject'];

interface UseProjectSelectOptions {
  projects?: any;
  withPersonal?: boolean;
  onlyRetainer?: boolean;
  onlyActive?: boolean;
  withRequests?: boolean;
}

const useProjectSelect = ({ projects, withPersonal, onlyRetainer, onlyActive, withRequests = true }: UseProjectSelectOptions) => {
  const dispatch = useDispatch();

  const options = useMemo(() => {
    const filterProject = (project: any) => {
      return (
        (withPersonal || project.kind !== 'personal') &&
        (!onlyRetainer || project.is_retainer) &&
        (!onlyActive || project.status === 'active')
      );
    };

    const sortProject = (a: any, b: any) => {
      if ((a.status === 'active' && b.status === 'active') || (a.status !== 'active' && b.status !== 'active')) {
        const aDisplay = parseProjectTitle(a);
        const bDisplay = parseProjectTitle(b);
        return aDisplay.localeCompare(bDisplay);
      }
      return a.status === 'active' ? -1 : 1;
    };

    const result: any[] = [];

    const sortedProjects = projects?.filter(filterProject).sort(sortProject) ?? [];

    sortedProjects.forEach((project: any) => {
      const { requests = [], ...projectWithoutRequests } = project;

      result.push({
        value: project.uuid,
        display: parseProjectTitle(project),
        extra: projectWithoutRequests,
        label: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '5px 12px',
              background: project.status === 'cancelled' || project.status === 'completed' ? '#e4e4e4' : undefined,
            }}
          >
            <Row justify="space-between">
              {project.customer?.business_name}
              <StatusTag value="plan" />
            </Row>
            <span className="sub">{parseProjectTitle(project)}</span>
          </div>
        ),
      });

      if (withRequests) {
        requests?.forEach((request: any) => {
          result.push({
            value: request.uuid,
            display: request.subject,
            extra: { ...request, project },
            label: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '5px 12px 5px 32px',
                }}
              >
                <Row justify="space-between" align="middle">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingRight: '10px',
                      }}
                    >
                      {request.subject}
                    </Text>
                  </div>
                  <StatusTag value="request" />
                </Row>
                <span className="sub">{request.title}</span>
              </div>
            ),
          });
        });
      }
    });

    return result;
  }, [projects, onlyActive, onlyRetainer, withPersonal, withRequests]);

  const onDropdownVisibleChangeHandler = (open: boolean) => {
    if (open) {
      dispatch(getProjectSelect());
    }
  };

  const filterOption = (search: string, option: any) => {
    const extra = option?.extra;
    const lowerSearch = search.toLowerCase();

    const matchesOwnKeys = searchKeys.some((key) => (get(extra, key) ?? '').toLowerCase().includes(lowerSearch));

    const matchesDisplay = (option.display ?? '').toLowerCase().includes(lowerSearch);

    if (extra.project) {
      const matchesProjectKeys = searchKeys.some((key) => (get(extra.project, key) ?? '').toLowerCase().includes(lowerSearch));
      return matchesOwnKeys || matchesProjectKeys || matchesDisplay;
    }

    return matchesOwnKeys || matchesDisplay;
  };

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.projects.project',
      callback: () => dispatch(getProjectSelect()),
    },
  });

  return {
    options,
    onDropdownVisibleChangeHandler,
    filterOption,
  };
};

export default useProjectSelect;
